import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import SafeIcon from '../../common/SafeIcon';
import Layout from '../Layout';
import * as FiIcons from 'react-icons/fi';

const { FiMail, FiMessageSquare } = FiIcons;

const ContactPage = () => {
  const { t, language } = useLanguage();
  const { isDark } = useTheme();

  // Direct inline style for title color
  const titleStyle = {
    fontWeight: 700,
    color: isDark ? '#0ea5e9' : '#6b21a8' // sky-500 for dark, purple-800 for light
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <Layout>
      <div className="space-y-6">
        <Helmet>
          <title>{language === 'zh' ? '聯絡我們 | Beat Buddy 節拍器' : 'Contact Us | Beat Buddy Metronome'}</title>
          <meta name="description" content={language === 'zh' ? "與 Beat Buddy 團隊聯絡，分享您的反饋、問題或建議。我們很樂意聽取您的意見，幫助您獲得最佳的音樂練習體驗。" : "Contact the Beat Buddy team, share your feedback, questions, or suggestions. We're happy to hear your thoughts and help you get the best music practice experience."} />
          <meta name="keywords" content="Beat Buddy, contact us, customer support, feedback, metronome app, contact form" />
          <link rel="canonical" href="https://beatbuddy.app/contact" />
        </Helmet>

        <div className="text-center max-w-xl mx-auto">
          <h2 className="text-3xl mb-2">
            <span style={titleStyle}>{t('contactTitle')}</span>
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300 mx-auto">
            {t('contactSubtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6">
          <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-xl">
            <h3 className="text-xl font-semibold mb-6 text-purple-600 dark:text-purple-400">
              {t('contactMethods')}
            </h3>

            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={FiMail} className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <h4 className="font-semibold text-lg mb-1 text-gray-800 dark:text-gray-200">{t('emailContact')}</h4>
                  <p className="text-gray-600 dark:text-gray-300">info@homy.hk</p>
                </div>
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mt-8">
                <h4 className="font-semibold text-lg mb-4 text-gray-800 dark:text-gray-200">{t('faqTitle')}</h4>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  {t('feedbackValued')}
                </p>
                <ul className="space-y-2">
                  <li>
                    <Link to="/help" onClick={scrollToTop} className="text-purple-600 dark:text-purple-400 hover:underline">
                      {t('howToUnlock')}
                    </Link>
                  </li>
                  <li>
                    <Link to="/help" onClick={scrollToTop} className="text-purple-600 dark:text-purple-400 hover:underline">
                      {t('dataSaving')}
                    </Link>
                  </li>
                  <li>
                    <Link to="/help" onClick={scrollToTop} className="text-purple-600 dark:text-purple-400 hover:underline">
                      {t('howToSetAccent')}
                    </Link>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/30 dark:to-pink-900/30 rounded-xl p-4 mt-4">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {t('feedbackValued')}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-xl mt-6">
            <h3 className="text-xl font-semibold mb-6 text-center text-purple-600 dark:text-purple-400">
              {t('faqTitle')}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-xl">
                  <h4 className="font-semibold mb-2 text-gray-800 dark:text-gray-200">{t('isBeatBuddyFree')}</h4>
                  <p className="text-gray-600 dark:text-gray-300">
                    {t('isBeatBuddyFreeAnswer')}
                  </p>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-xl">
                  <h4 className="font-semibold mb-2 text-gray-800 dark:text-gray-200">{t('offlineUsage')}</h4>
                  <p className="text-gray-600 dark:text-gray-300">
                    {t('offlineUsageAnswer')}
                  </p>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-xl">
                  <h4 className="font-semibold mb-2 text-gray-800 dark:text-gray-200">{t('supportedDevices')}</h4>
                  <p className="text-gray-600 dark:text-gray-300">
                    {t('supportedDevicesAnswer')}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-xl">
                  <h4 className="font-semibold mb-2 text-gray-800 dark:text-gray-200">{t('requestFeature')}</h4>
                  <p className="text-gray-600 dark:text-gray-300">
                    {t('requestFeatureAnswer')}
                  </p>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-xl">
                  <h4 className="font-semibold mb-2 text-gray-800 dark:text-gray-200">{t('reportBug')}</h4>
                  <p className="text-gray-600 dark:text-gray-300">
                    {t('reportBugAnswer')}
                  </p>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-xl">
                  <h4 className="font-semibold mb-2 text-gray-800 dark:text-gray-200">{t('mobileApp')}</h4>
                  <p className="text-gray-600 dark:text-gray-300">
                    {t('mobileAppAnswer')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ContactPage;